import type { FormNode } from '../types';
import { extractFilters, toNumber, toString } from './parse-helpers';

/**
 * Parse form submission conditions.
 * Handles: { event: { type: 'event', name: 'form_submit', filters: { and: [{ field: 'form_id', value }, ...] } } }
 *          { type: 'form', name, times, within, inverse }
 */
export function parseForm(raw: Record<string, unknown>): FormNode | null {
  // Ember format: event.name === 'form_submit'
  const event =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    raw.event as Record<string, unknown> | undefined;
  if (
    event?.name === 'form_submit' ||
    event?.type === 'form_submit' ||
    event?.type === 'form' ||
    event?.ui_type === 'form'
  ) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const filters = event.filters as Record<string, unknown> | undefined;
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const andFilters = (filters?.and ?? filters?.or) as Record<string, unknown>[] | undefined;

    // Extract form name from form_id filter
    let formName: string | undefined;
    const remaining: Record<string, unknown>[] = [];

    if (andFilters != null) {
      for (const f of andFilters) {
        if (f.field === 'form_id' && f.value != null) {
          formName = toString(f.value);
        } else {
          remaining.push(f);
        }
      }
    }

    // Check for enriched form name
    formName = toString(raw.form_name) ?? formName;
    const parsed = remaining.length > 0 ? extractFilters(remaining) : null;

    // Backend format stores inverse/times/within in event_filter, not at the top level
    const eventFilter =
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      raw.event_filter as Record<string, unknown> | undefined;

    return {
      type: 'form',
      formName,
      inverse: (eventFilter?.inverse ?? raw.inverse) === true,
      times: toNumber(eventFilter?.times ?? raw.times),
      within: toNumber(eventFilter?.within ?? raw.within),
      filters: parsed?.filters,
      filtersOperator: parsed?.operator,
    };
  }

  // Direct format
  if (raw.type === 'form') {
    const parsed = extractFilters(raw.filters);
    return {
      type: 'form',
      formName: toString(raw.name ?? raw.form_id),
      inverse: raw.inverse === true,
      times: toNumber(raw.times),
      within: toNumber(raw.within),
      filters: parsed?.filters,
      filtersOperator: parsed?.operator,
    };
  }

  return null;
}
